/**
 * =============================================================================
 * LOGGER UTILITY - ADMIN UI
 * =============================================================================
 *
 * Structured logging utility for the admin UI.
 * Prefixes all messages with [FindMyPart Admin] for easy filtering.
 *
 * In production, only warn and error levels are logged to reduce noise.
 * Unlike the storefront widget, admin-ui does NOT have drop_console in its
 * build config, so log level filtering is done at runtime.
 *
 * Features:
 * - Environment-aware log level filtering (debug/info only in development)
 * - Structured context support for better debugging
 * - Error object handling with stack traces
 * - Consistent prefix formatting for browser console filtering
 *
 * =============================================================================
 */

const PREFIX = '[FindMyPart Admin]';
const isDevelopment = import.meta.env?.MODE === 'development';

/**
 * Structured context for log entries.
 */
export type LogContext = Record<string, unknown>;

/**
 * Logger interface matching the worker and widget logger pattern.
 */
export interface Logger {
  debug: (message: string, context?: LogContext) => void;
  info: (message: string, context?: LogContext) => void;
  warn: (message: string, context?: LogContext) => void;
  error: (message: string, error?: Error | null, context?: LogContext) => void;
}

/**
 * Format context object for console output.
 * Returns empty string if context is empty or undefined.
 */
function formatContext(context?: LogContext): string {
  if (!context || Object.keys(context).length === 0) return '';
  return ' ' + JSON.stringify(context);
}

/**
 * Admin UI logger instance.
 *
 * @example
 * ```ts
 * import { logger } from '../../lib/logger';
 *
 * // Debug logging (development only)
 * logger.debug('FETCHING_QUESTIONS', { page: 1 });
 *
 * // Info logging (development only)
 * logger.info('SESSION_RESTORED', { storeHash: 'abc123' });
 *
 * // Warning logging (always logged)
 * logger.warn('DEPRECATED_API_USED', { endpoint: '/api/old' });
 *
 * // Error logging with Error object (always logged)
 * logger.error('APPROVE_FAILURE', err, { questionId: 'q123' });
 * ```
 */
export const logger: Logger = {
  /**
   * Log debug message (development only).
   * Suppressed in production to reduce noise.
   */
  debug(message: string, context?: LogContext): void {
    if (isDevelopment) {
      // eslint-disable-next-line no-console
      console.log(`${PREFIX} [DEBUG] ${message}${formatContext(context)}`);
    }
  },

  /**
   * Log info message (development only).
   * Suppressed in production to reduce noise.
   */
  info(message: string, context?: LogContext): void {
    if (isDevelopment) {
      // eslint-disable-next-line no-console
      console.log(`${PREFIX} ${message}${formatContext(context)}`);
    }
  },

  /**
   * Log warning message.
   * Always logged (production and development).
   */
  warn(message: string, context?: LogContext): void {
    // eslint-disable-next-line no-console
    console.warn(`${PREFIX} ${message}${formatContext(context)}`);
  },

  /**
   * Log error message with optional Error object.
   * Error stack traces are included in context when provided.
   * Always logged (production and development).
   */
  error(message: string, error?: Error | null, context?: LogContext): void {
    const errorInfo = error ? { errorMessage: error.message, stack: error.stack } : {};
    const fullContext = { ...context, ...errorInfo };
    // eslint-disable-next-line no-console
    console.error(`${PREFIX} ${message}${formatContext(fullContext)}`);
  },
};
